import { useEffect, useRef, useState } from 'react';
import { calcImpact } from '@/lib/impactCalculator';
import type { SolutionSlug } from '@/lib/solutions.config';

let _open: (slug: SolutionSlug)=>void;
export function openImpactModal(slug: SolutionSlug){ _open?.(slug); }

export default function ImpactModalRoot(){
  const [open,setOpen] = useState(false);
  const [slug,setSlug] = useState<SolutionSlug>('empleados-digitales');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(()=>{ 
    _open = (s)=>{ 
      setSlug(s); 
      setOpen(true); 
      document.body.style.overflow='hidden'; 
    }; 
  },[]);
  
  const close = ()=>{ 
    setOpen(false); 
    document.body.style.overflow=''; 
    setStep(1);
    setForm({ teamSize: 5, systems: [], pain: 'tiempo', goals: [] });
  };

  useEffect(()=>{
    const onEsc = (e: KeyboardEvent)=> e.key==='Escape' && close();
    if(open) {
      window.addEventListener('keydown',onEsc);
      // Focus trap
      dialogRef.current?.focus();
    }
    return ()=> window.removeEventListener('keydown',onEsc);
  },[open]);

  // Estado del stepper
  const [step,setStep] = useState(1);
  const [form,setForm] = useState({ 
    teamSize: 5, 
    systems: [] as string[], 
    pain: 'tiempo' as 'tiempo'|'errores'|'crecimiento', 
    goals: [] as string[] 
  });

  const result = step===4 ? calcImpact(slug, form) : null;

  if(!open) return null;

  return (
    <div className="modal" onClick={close} role="dialog" aria-modal="true">
      <div 
        className="modal__dialog" 
        onClick={e=>e.stopPropagation()} 
        ref={dialogRef}
        tabIndex={-1}
      >
        <header className="modal__header">
          <h3 id="modal-title">Diagnóstico Exprés ({step}/3)</h3>
          <button 
            onClick={close}
            className="absolute top-4 right-4 text-muted hover:text-foreground"
            aria-label="Cerrar modal"
          >
            ✕
          </button>
        </header>

        {step===1 && (
          <div className="modal__body">
            <label htmlFor="team-size" className="block text-sm font-medium mb-2">
              Tamaño del equipo
            </label>
            <input 
              id="team-size"
              type="number" 
              min={1} 
              max={100}
              value={form.teamSize}
              onChange={e=>setForm({...form, teamSize:+e.target.value})}
              className="w-full p-2 border border-input rounded-md"
            />
            
            <label className="block text-sm font-medium mt-4 mb-2">Dolor principal</label>
            <div className="choices">
              {[
                {key: 'tiempo', label: 'Pérdida de tiempo en tareas manuales'},
                {key: 'errores', label: 'Errores frecuentes en procesos'},
                {key: 'crecimiento', label: 'Dificultad para escalar el negocio'}
              ].map(({key, label})=>(
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="pain"
                    checked={form.pain===key} 
                    onChange={()=>setForm({...form,pain:key as any})}
                    className="w-4 h-4"
                  /> 
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {step===2 && (
          <div className="modal__body">
            <label className="block text-sm font-medium mb-2">Sistemas actuales en uso</label>
            <div className="choices">
              {[
                {key: 'excel', label: 'Excel/Google Sheets'},
                {key: 'erp', label: 'ERP'},
                {key: 'crm', label: 'CRM'},
                {key: 'pos', label: 'POS/Punto de Venta'},
                {key: 'ecom', label: 'E-commerce'}
              ].map(({key, label})=>(
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={form.systems.includes(key)}
                    onChange={(e)=>{
                      const has = form.systems.includes(key);
                      setForm({...form, systems: has ? form.systems.filter(x=>x!==key) : [...form.systems,key]});
                    }}
                    className="w-4 h-4"
                  /> 
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {step===3 && (
          <div className="modal__body">
            <label className="block text-sm font-medium mb-2">Objetivos principales</label>
            <div className="choices">
              {[
                {key: 'ahorrar-tiempo', label: 'Ahorrar tiempo en procesos'},
                {key: 'reducir-errores', label: 'Reducir errores críticos'},
                {key: 'mejorar-ventas', label: 'Mejorar efectividad de ventas'},
                {key: 'visibilidad', label: 'Mayor visibilidad de datos'},
                {key: 'escalabilidad', label: 'Preparar para crecimiento'}
              ].map(({key, label})=>(
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={form.goals.includes(key)}
                    onChange={()=>{
                      const has = form.goals.includes(key);
                      setForm({...form, goals: has ? form.goals.filter(x=>x!==key) : [...form.goals,key]});
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {step===4 && result && (
          <div className="modal__body">
            <h4 className="text-lg font-semibold mb-4">Tu impacto estimado</h4>
            <div className="kpis">
              <div className="kpi">
                <div className="kpi__val">{result.hoursSaved}h/sem</div>
                <div className="kpi__label">Horas ahorradas</div>
              </div>
              <div className="kpi">
                <div className="kpi__val">-{result.errorReduction}%</div>
                <div className="kpi__label">Errores</div>
              </div>
              <div className="kpi">
                <div className="kpi__val">{result.roiX}x</div>
                <div className="kpi__label">ROI en 90 días</div>
              </div>
            </div>
            <div className="mt-4">
              <h5 className="font-medium mb-2">Plan sugerido:</h5>
              <ul className="plan space-y-1">
                {result.plan.map(p=><li key={p} className="text-sm">• {p}</li>)}
              </ul>
            </div>
            <p className="text-xs text-muted mt-4">
              *Estimación basada en casos típicos; confirmamos en el diagnóstico.
            </p>
          </div>
        )}

        <footer className="modal__actions">
          {step>1 && step<4 && (
            <button 
              className="btn ghost" 
              onClick={()=>setStep(step-1)}
            >
              Atrás
            </button>
          )}
          {step<3 && (
            <button 
              className="btn-cta" 
              onClick={()=>{ 
                if (typeof window !== 'undefined' && window.gtag) {
                  window.gtag('event','diag_step',{step:step+1, slug}); 
                }
                setStep(step+1); 
              }}
            >
              Siguiente
            </button>
          )}
          {step===3 && (
            <button 
              className="btn-cta" 
              onClick={()=>{ 
                if (typeof window !== 'undefined' && window.gtag) {
                  window.gtag('event','diag_submit',{slug, ...calcImpact(slug, form).analytics}); 
                }
                setStep(4); 
              }}
            >
              Ver estimación
            </button>
          )}
          {step===4 && (
            <>
              <a 
                className="btn-cta" 
                href={`/soluciones/${slug}#agenda?utm_source=diag&utm_medium=modal`}
                onClick={() => {
                  if (typeof window !== 'undefined' && window.gtag) {
                    window.gtag('event','diag_agenda_click',{slug, source:'modal_result'});
                  }
                }}
              >
                Agendar 30 min
              </a>
              <a className="btn secondary" href={`/soluciones/${slug}`}>
                Ver cómo funciona
              </a>
              <button className="btn ghost" onClick={close}>
                Cerrar
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}